import React, { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, FormControl, Select, MenuItem, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { motion, useInView } from "framer-motion";
import { register as registerApi } from "../../services/auth";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

// Exemples de numéros locaux les plus courants par pays (affichés en placeholder, non bloquants)
const countryPhoneExamples = {
  "+33": "612345678",
  "+32": "471234567",
  "+41": "781234567",
  "+212": "612345678",
  "+216": "20123456",
  "+213": "551234567",
  "+225": "01123456",
  "+221": "771234567",
  "+237": "697550022",
  "+34": "612345678",
  "+39": "3123456789",
  "+44": "7123456789",
  "+1": "2025550123",
  "+49": "15123456789"
};

const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  return {
    isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
    minLength: password.length >= minLength,
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar
  };
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 }
  }
};

const floatingAnimation = {
  y: [0, -10, 0],
  transition: {
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)'
};

export default function LandingPage() {
  const signupRef = useRef(null);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const keysRef = useRef(null);
  const pricingRef = useRef(null);
  
  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" });
  const keysInView = useInView(keysRef, { once: true, margin: "-100px" });
  const pricingInView = useInView(pricingRef, { once: true, margin: "-100px" });
  
  const navigate = useNavigate();
  const [values, setValues] = useState({ lastName: "", firstName: "", email: "", countryCode: "+33", localPhone: "", password: "", confirmPassword: "", plan: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  

  const scrollToSignup = useCallback((e) => {
    if (e) e.preventDefault();
    if (signupRef.current) {
      signupRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const setField = (name) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [name]: value }));
    
    // Mettre à jour la force du mot de passe en temps réel
    if (name === 'password' && value) {
      setPasswordStrength(validatePassword(value));
    }
  };

  const validate = () => {
    const next = {};
    if (!values.lastName.trim()) next.lastName = "Le nom est requis.";
    if (!values.firstName.trim()) next.firstName = "Le prénom est requis.";
    if (!values.email.trim()) next.email = "L'email est requis.";
    else if (!validateEmail(values.email)) next.email = "Email invalide (exemple@domaine.com).";
    if (!values.countryCode || !/^\+\d{1,3}$/.test(values.countryCode)) next.phone = "Sélectionnez un indicatif valide.";
    const digitsOnly = values.localPhone.replace(/\D/g, '');
    if (!digitsOnly) {
      next.phone = "Le numéro de téléphone est requis.";
    } else if (digitsOnly.length < 6 || digitsOnly.length > 15) {
      // Longueur invalide pour un format international valide
      next.phone = "Numéro invalide pour ce pays.";
    }
    if (!values.password) next.password = "Le mot de passe est requis.";
    else {
      const passwordValidation = validatePassword(values.password);
      if (!passwordValidation.isValid) {
        next.password = "Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.";
      }
    }
    if (!values.confirmPassword) next.confirmPassword = "La confirmation du mot de passe est requise.";
    else if (values.password !== values.confirmPassword) next.confirmPassword = "Les mots de passe doivent être identiques.";
    if (!values.plan) next.plan = "Veuillez choisir un abonnement.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fullPhone = `${values.countryCode}${values.localPhone.replace(/\D/g, '')}`;
      await registerApi(values.email, values.email, values.password, values.firstName, values.lastName, fullPhone, values.plan);
      setSuccessOpen(true);
      setValues({ lastName: "", firstName: "", email: "", countryCode: "+33", localPhone: "", password: "", confirmPassword: "", plan: "" });
    } catch (err) {
      setSubmitError(err?.message || "Échec de l'inscription. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const phoneHelperText = errors.phone || "Entrez entre 6 et 15 chiffres.";
  const phonePlaceholder =
    values.countryCode && countryPhoneExamples[values.countryCode]
      ? countryPhoneExamples[values.countryCode]
      : "";

  return (
    <Box 
      sx={{ 
        width: '100%', 
        m: 0, 
        p: 0, 
        // Autoriser le scroll horizontal sur mobile pour éviter que le tableau soit coupé
        overflowX: { xs: 'auto', md: 'hidden' } 
      }}
    >
      {/* Header with Glassmorphism */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bgcolor: 'rgba(255, 255, 255, 0.8)', 
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
          zIndex: 10 
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5 }}>
            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
            <Box component="img" src="/qilinsa-amp-logo.png" alt="Qilinsa Amplify" sx={{ width: 32, height: 32, objectFit: 'contain' }} />
            <Typography variant="h6" color="primary" fontWeight={700}>Qilinsa Amplify</Typography>
          </Box>
            </motion.div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button 
                onClick={() => navigate('/login')} 
                sx={{ 
                  color: '#4B5563', 
                  textTransform: 'none', 
                  '&:hover': { color: '#4F46E5', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease'
                }}
              >
                Se connecter
              </Button>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button onClick={scrollToSignup} variant="contained" sx={{ textTransform: 'none' }}>
                  Créer un compte
                </Button>
              </motion.div>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* Hero with Animations */}
      <Box 
        ref={heroRef}
        sx={{ 
          mt: 5, 
          textAlign: 'center', 
          py: 10, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3), transparent 50%), radial-gradient(circle at 80% 80%, rgba(138, 43, 226, 0.3), transparent 50%)',
            animation: 'pulse 8s ease-in-out infinite'
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Typography variant="h3" fontWeight={700} sx={{ mb: 2 }}>
            Donnez un coup d'accélérateur à votre marketing
          </Typography>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Typography variant="h6" sx={{ mb: 4 }}>
            Qilinsa Amplify simplifie vos campagnes, automatise vos workflows, booste vos résultats et renforce la fidélité client.
          </Typography>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <motion.div
            animate={floatingAnimation}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={scrollToSignup} 
              sx={{ 
                background: '#fff', 
                color: '#4F46E5', 
                fontWeight: 600, 
                px: 4, 
                py: 1.2, 
                textTransform: 'none', 
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                '&:hover': { 
                  background: '#f1f5f9',
                  boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)'
                } 
              }}
            >
              Créer mon compte gratuit
            </Button>
          </motion.div>
        </motion.div>
      </Box>

      {/* Pourquoi choisir with Glassmorphism */}
      <Box 
        ref={featuresRef}
        sx={{ 
          py: 8, 
          px: 0, 
          width: '100%',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%)'
        }}
      >
        <Box sx={{ px: 3, maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>
              Pourquoi choisir Qilinsa Amplify ?
            </Typography>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate={featuresInView ? "animate" : "initial"}
          >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {[{ h: 'Centralisez vos données', p: 'Gérez vos contacts, segments et campagnes au même endroit.' },
            { h: 'Automatisez vos workflows', p: 'Créez des scénarios marketing puissants avec un constructeur visuel.' },
                { h: 'Optimisez avec l\'IA', p: 'Bénéficiez de recommandations intelligentes pour améliorer vos campagnes.' },
                { h: 'Programme de fidélité exclusif', p: 'Récompensez vos clients et boostez la rétention grâce à notre programme de fidélité intégré.' }].map((item, index) => (
                <motion.div
                  key={item.h}
                  initial={{ opacity: 0, y: 30 }}
                  animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                >
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: '#fff', 
                    borderRadius: 3, 
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    '&:hover': {
                      boxShadow: '0 15px 45px rgba(79, 70, 229, 0.2)',
                      borderColor: 'rgba(79, 70, 229, 0.3)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#4F46E5' }}>
                      {item.h}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      {item.p}
                    </Typography>
            </Box>
                </motion.div>
          ))}
        </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Fonctionnalités clés with Glassmorphism */}
      <Box 
        ref={keysRef}
        sx={{ 
          py: 8, 
          px: 0, 
          width: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative'
        }}
      >
        <Box sx={{ px: 3, maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={keysInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: '#fff' }}>
              Fonctionnalités clés
            </Typography>
          </motion.div>
          
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate={keysInView ? "animate" : "initial"}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, textAlign: 'left' }}>
            {[{ h: 'Gestion de contacts', p: 'Base de données unifiée avec suivi des statuts et comportements.' },
              { h: 'Campagnes multicanales', p: 'Email, SMS, tout dans un seul outil.' },
              { h: 'Éditeur intelligent', p: 'Templates modernes, personnalisation et suggestions IA.' },
              { h: 'Programme de fidélité client', p: 'Concevez des programmes exclusifs de récompenses et créez un lien durable avec vos clients.' },
                { h: 'Analytique avancée', p: 'Analyse des taux d\'ouverture, clics, A/B tests, segments.' },
              { h: 'IA intégrée', p: 'Suggestions en temps réel pour maximiser vos performances.' },
                { h: 'Analyse de fidélité intelligente', p: 'Identifiez vos clients les plus fidèles et récompensez-les grâce à des programmes personnalisés.' }].map((item, index) => (
                <motion.div
                  key={item.h}
                  initial={{ opacity: 0, y: 30 }}
                  animate={keysInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
                >
                  <Box sx={{ 
                    p: 3, 
                    ...glassStyle,
                    borderRadius: 3,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 15px 45px rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.15)'
                    }
                  }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#fff' }}>
                      {item.h}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      {item.p}
                    </Typography>
              </Box>
                </motion.div>
            ))}
          </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Plans with Glassmorphism */}
      <Box 
        ref={pricingRef}
        sx={{ 
          py: 8, 
          px: 0, 
          width: '100%',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%)'
        }}
      >
        <Box sx={{ px: 3, maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={pricingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>
              Nos abonnements
            </Typography>
          </motion.div>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          {/* Starter */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={pricingInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 15px 45px rgba(79, 70, 229, 0.15)'
                }
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#4F46E5' }}>Démo (14j)</Typography>
                <Typography sx={{ mb: 2, color: '#6B7280', fontSize: '0.9rem' }}>Idéal pour les petites entreprises explorant l'automatisation.</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#1F2937' }}>Gratuit</Typography>
                <Box component="ul" sx={{ pl: 2, textAlign: 'left', mb: 3, flexGrow: 1, fontSize: '0.875rem', color: '#4B5563' }}>
              <li> Import manuel de contacts</li>
              <li> Création de campagnes IA</li>
                  <li> Jusqu'à 1000 contacts</li>
              <li> A/B Testing (Email)</li>
              <li> Édition d’emails (IA & manuelle)</li>
            </Box>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button onClick={scrollToSignup} variant="contained" sx={{ textTransform: 'none', width: '100%' }}>Commencer</Button>
                </motion.div>
          </Box>
            </motion.div>
            
          {/* Growth */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={pricingInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 15px 45px rgba(79, 70, 229, 0.15)'
                }
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#4F46E5' }}>Growth</Typography>
                <Typography sx={{ mb: 2, color: '#6B7280', fontSize: '0.9rem' }}>Parfait pour les PME en pleine croissance.</Typography><br/>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#1F2937' }}>49.000 FCFA / $89</Typography>
                <Box component="ul" sx={{ pl: 2, textAlign: 'left', mb: 3, flexGrow: 1, fontSize: '0.875rem', color: '#4B5563' }}>
              <li> Toutes les fonctionnalités Démo</li>
              <li> Tracking visiteurs / clients</li>
                  <li> Assistant de campagne manuel</li>
              <li> Intégration sociale (Facebook, Insta, WhatsApps) (add-on)</li>
                  <li> Jusqu'à 5 000 contacts</li>
              <li> Tableaux de bord & KPIs essentiels</li>
            </Box>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button onClick={scrollToSignup} variant="contained" sx={{ textTransform: 'none', width: '100%' }}>Commencer</Button>
                </motion.div>
          </Box>
            </motion.div>
            
            {/* Pro - Highlighted */}
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={pricingInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -15, scale: 1.02, transition: { duration: 0.3 } }}
            >
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 3, 
                boxShadow: '0 15px 50px rgba(79, 70, 229, 0.3)',
                border: '2px solid #4F46E5',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 20px 60px rgba(79, 70, 229, 0.4)'
                },
                '&::before': {
                  content: '"POPULAIRE"',
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: '#4F46E5',
                  color: '#fff',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 700
                }
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#4F46E5' }}>Pro</Typography>
                <Typography sx={{ mb: 2, color: '#6B7280', fontSize: '0.9rem' }}>Pour les entreprises en expansion avec des besoins avancés.</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#4F46E5' }}>99.000 FCFA / $179</Typography>
                <Box component="ul" sx={{ pl: 2, textAlign: 'left', mb: 3, flexGrow: 1, fontSize: '0.875rem', color: '#4B5563' }}>
              <li> Toutes les fonctionnalités Growth</li>
              <li> Réconciliation automatique des profils</li>
              <li> Intégrations & API personnalisées</li>
              <li> Prévisualisation & test anti-spam</li>
              <li> Chat IA basique</li>
                  <li> Jusqu'à 25 000 contacts</li>
              
            </Box>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    onClick={scrollToSignup} 
                    variant="contained" 
                    sx={{ 
                      textTransform: 'none', 
                      width: '100%',
                      bgcolor: '#4F46E5',
                      '&:hover': { bgcolor: '#4338CA' }
                    }}
                  >
                    Commencer
                  </Button>
                </motion.div>
          </Box>
            </motion.div>
            
          {/* Enterprise */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={pricingInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 15px 45px rgba(79, 70, 229, 0.15)'
                }
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#4F46E5' }}>Enterprise</Typography>
                <Typography sx={{ mb: 2, color: '#6B7280', fontSize: '0.9rem' }}>Solutions personnalisées pour grandes entreprises.</Typography><br/>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#1F2937' }}>Sur devis</Typography>
                <Box component="ul" sx={{ pl: 2, textAlign: 'left', mb: 3, flexGrow: 1, fontSize: '0.875rem', color: '#4B5563' }}>
              <li> Toutes les fonctionnalités Pro</li>
              <li> Intégration auto depuis landing pages</li>
              <li> Campagnes SMS (add-on)</li>
              <li> Analyse IA avancée des segments</li>
              <li> Contacts illimités</li>
              <li> Gestion du programme de fidélité</li>
              <li> Multi-brand / multi-tenant </li>
            </Box>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button onClick={scrollToSignup} variant="contained" sx={{ textTransform: 'none', width: '100%' }}>Nous contacter</Button>
                </motion.div>
          </Box>
            </motion.div>
        </Box>
        </Box>
      </Box>


      {/* Plans with Comparison Table */}
      <Box 
        ref={pricingRef}
        sx={{ 
          py: 8, 
          px: 0, 
          width: '100%',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%)'
        }}
      >
        <Box sx={{ px: 3, maxWidth: 1400, mx: 'auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={pricingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>
            Fonctionnalités
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={pricingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Wrapper pour permettre un scroll horizontal du tableau uniquement sur petits écrans */}
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  maxHeight: showAllFeatures ? 'none' : 420,
                  minWidth: 650
                }}
              >
              <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      bgcolor: '#4F46E5',
                      '& .MuiTableCell-root': {
                        py: 2.5,
                        px: 2,
                        borderRight: '1px solid rgba(255,255,255,0.15)',
                        '&:last-of-type': {
                          borderRight: 'none'
                        }
                      }
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 700, 
                        fontSize: '1rem', 
                        width: '40%',
                        whiteSpace: 'normal'
                      }}
                    >
                      Fonctionnalités
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 700, 
                        fontSize: '1rem', 
                        width: '15%',
                        whiteSpace: 'normal'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700 }}>Démo (14 jours)</Typography>
                        <Typography sx={{ color: '#fff', opacity: 0.9 }}>Gratuit</Typography>
                        <Button onClick={scrollToSignup} variant="contained" size="small" sx={{ textTransform: 'none' }}>Commencer</Button>
                      </Box>
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 700, 
                        fontSize: '1rem', 
                        width: '15%',
                        whiteSpace: 'normal'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700 }}>Growth</Typography>
                        <Typography sx={{ color: '#fff', opacity: 0.9 }}>49.000 FCFA / $89</Typography>
                        <Button onClick={scrollToSignup} variant="contained" size="small" sx={{ textTransform: 'none' }}>Commencer</Button>
                      </Box>
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 700, 
                        fontSize: '1rem', 
                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                        width: '15%',
                        whiteSpace: 'normal'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ color: '#fff', fontWeight: 700, mr: 1 }}>Pro</Typography>
                          <Chip label="Populaire" size="small" sx={{ bgcolor: '#fff', color: '#4F46E5', fontWeight: 700, fontSize: '0.75rem', height: 20 }} />
                        </Box>
                        <Typography sx={{ color: '#fff', opacity: 0.9 }}>99.000 FCFA / $179</Typography>
                        <Button onClick={scrollToSignup} variant="contained" size="small" sx={{ textTransform: 'none', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>Commencer</Button>
                      </Box>
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 700, 
                        fontSize: '1rem', 
                        width: '15%',
                        whiteSpace: 'normal'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700 }}>Enterprise</Typography>
                        <Typography sx={{ color: '#fff', opacity: 0.9 }}>Sur devis</Typography>
                        <Button onClick={scrollToSignup} variant="contained" size="small" sx={{ textTransform: 'none' }}>Nous contacter</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  
                  
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, py: 2, color: '#4F46E5' }}>MARKETING AUTOMATION & CAMPAGNES</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Création de campagnes IA</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Assistant de campagne manuel</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Édition d'emails (IA & manuelle)</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>A/B Testing</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Prévisualisation & test anti-spam</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Campagnes SMS (add-on)</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Suppression logo Qilinsa Email (add-on)</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Intégration sociale (Facebook, Insta, WhatsApp) (add-on)</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, py: 2, color: '#4F46E5' }}>IA & ANALYTICS</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Analyse IA des campagnes & suggestions</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Analyse IA avancée des segments</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Tableaux de bord & KPIs essentiels</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Tableaux de bord avancés avec IA</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, py: 2, color: '#4F46E5' }}>CDP & CRM</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Vue 360° client</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Import manuel de contacts</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Réconciliation automatique des profils</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Tracking visiteurs / clients</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Intégration auto depuis landing pages</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Intégrations & API personnalisées</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, py: 2, color: '#4F46E5' }}>CONTACTS & VOLUMES</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre maximum de contacts</TableCell>
                    <TableCell align="center">1 000</TableCell>
                    <TableCell align="center">5 000</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>25 000</TableCell>
                    <TableCell align="center">Illimité</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Volume d'emails inclus / mois</TableCell>
                    <TableCell align="center">1 000</TableCell>
                    <TableCell align="center">15 000</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>75 000</TableCell>
                    <TableCell align="center">Personnalisé / dédié</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Volume SMS inclus</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">100 SMS (add-on)</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>500 SMS (add-on)</TableCell>
                    <TableCell align="center">Sur devis</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Email additionnel</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">+2 000 emails = +5 000 FCFA</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>+10 000 emails = +10 000 FCFA</TableCell>
                    <TableCell align="center">Sur devis</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Limites d'actions journalières sur le chat</TableCell>
                    <TableCell align="center">3 actions / jour</TableCell>
                    <TableCell align="center">10 actions / jour</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>30 actions / jour</TableCell>
                    <TableCell align="center">Illimité</TableCell>
                  </TableRow>
                  
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, py: 2, color: '#4F46E5' }}>FIDÉLITÉ & ENGAGEMENT</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Gestion du programme de fidélité</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>✔️</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Multi-brand / multi-tenant</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, py: 2, color: '#4F46E5' }}>SUPPORT & SERVICES</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Support</TableCell>
                    <TableCell align="center">Standard</TableCell>
                    <TableCell align="center">Standard</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>Prioritaire</TableCell>
                    <TableCell align="center">Dédié (SLA)</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Gestionnaire de compte dédié</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center">–</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>–</TableCell>
                    <TableCell align="center">✔️</TableCell>
                  </TableRow>
                  
                  
                </TableBody>
              </Table>
                {!showAllFeatures && (
                  <Box 
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 80,
                      background: 'linear-gradient(to top, rgba(248,249,250,1) 0%, rgba(248,249,250,0.7) 40%, rgba(248,249,250,0) 100%)',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12
                    }}
                  />
                )}
              </TableContainer>
            </Box>
            <Box sx={{ mt: 2 }}>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  onClick={() => setShowAllFeatures((v) => !v)}
                  variant="outlined"
                  sx={{ textTransform: 'none' }}
                >
                  {showAllFeatures ? 'Voir moins' : 'Voir plus de fonctionnalités'}
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Signup Section with Animations */}
      <Box 
        ref={signupRef} 
        id="signup" 
        sx={{ 
          py: 8, 
          px: 0, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: '#fff', 
          textAlign: 'center', 
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(120, 119, 198, 0.3), transparent 50%)',
            animation: 'pulse 8s ease-in-out infinite'
          }
        }}
      >
        <Box sx={{ px: 3, position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              Rejoignez Qilinsa Amplify dès aujourd'hui
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography variant="body1" sx={{ mb: 3 }}>
              Créez votre compte gratuitement et commencez à transformer vos campagnes marketing.
            </Typography>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Box 
            component="form" 
            onSubmit={onSubmit} 
            sx={{ 
              maxWidth: 520, 
              mx: 'auto', 
              bgcolor: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              color: '#111827', 
              p: 4, 
              borderRadius: 3, 
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
          {submitError && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
            <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
              </motion.div>
            )}
            <TextField 
              fullWidth 
              label="Nom *" 
              value={values.lastName} 
              onChange={setField('lastName')} 
              error={!!errors.lastName} 
              helperText={errors.lastName} 
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                  }
                }
              }} 
            />
            <TextField 
              fullWidth 
              label="Prénom *" 
              value={values.firstName} 
              onChange={setField('firstName')} 
              error={!!errors.firstName} 
              helperText={errors.firstName} 
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                  }
                }
              }} 
            />
            <FormControl 
              fullWidth 
              error={!!errors.plan}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                  }
                }
              }}
            >
              <InputLabel id="plan-select-label">Choisir un abonnement *</InputLabel>
              <Select
                labelId="plan-select-label"
                label="Choisir un abonnement *"
                value={values.plan}
                onChange={(e) => setValues((v) => ({ ...v, plan: e.target.value }))}
              >
                <MenuItem value={"Demo"}>Démo (14j gratuits)</MenuItem>
                <MenuItem value={"Growth"}>Growth (49 000 FCFA/ 89$)</MenuItem>
                <MenuItem value={"Pro"}>Pro (99 000 FCFA / 179$)</MenuItem>
                <MenuItem value={"Enterprise"}>Enterprise (Sur devis)</MenuItem>
              </Select>
              {errors.plan && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.plan}
                </Typography>
              )}
            </FormControl>
            {values.plan === 'Enterprise' && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  Notre équipe vous contactera pour configurer votre offre personnalisée.
                </Alert>
              </motion.div>
            )}
            
            <TextField 
              fullWidth 
              label="Adresse email *" 
              type="email" 
              value={values.email} 
              onChange={setField('email')} 
              error={!!errors.email} 
              helperText={errors.email} 
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                  }
                }
              }} 
            />
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <FormControl 
              sx={{ 
                minWidth: 120,
                '& .MuiOutlinedInput-root': {
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                  }
                }
              }}
            >
              <InputLabel id="country-code-label">Indicatif *</InputLabel>
              <Select
                labelId="country-code-label"
                value={values.countryCode}
                label="Indicatif *"
                onChange={(e) => setValues((v) => ({ ...v, countryCode: e.target.value }))}
              >
                <MenuItem value={"+33"}>🇫🇷 +33 (France)</MenuItem>
                <MenuItem value={"+32"}>🇧🇪 +32 (Belgique)</MenuItem>
                <MenuItem value={"+41"}>🇨🇭 +41 (Suisse)</MenuItem>
                <MenuItem value={"+212"}>🇲🇦 +212 (Maroc)</MenuItem>
                <MenuItem value={"+216"}>🇹🇳 +216 (Tunisie)</MenuItem>
                <MenuItem value={"+213"}>🇩🇿 +213 (Algérie)</MenuItem>
                <MenuItem value={"+225"}>🇨🇮 +225 (Côte d'Ivoire)</MenuItem>
                <MenuItem value={"+221"}>🇸🇳 +221 (Sénégal)</MenuItem>
                <MenuItem value={"+237"}>🇨🇲 +237 (Cameroun)</MenuItem>
                <MenuItem value={"+34"}>🇪🇸 +34 (Espagne)</MenuItem>
                <MenuItem value={"+39"}>🇮🇹 +39 (Italie)</MenuItem>
                <MenuItem value={"+44"}>🇬🇧 +44 (Royaume-Uni)</MenuItem>
                <MenuItem value={"+1"}>🇺🇸 +1 (États‑Unis)</MenuItem>
                <MenuItem value={"+49"}>🇩🇪 +49 (Allemagne)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Numéro de téléphone *"
              value={values.localPhone}
              onChange={(e) => {
                // Autoriser uniquement chiffres, espaces et tirets comme séparateurs
                const cleaned = e.target.value.replace(/[^\d\s-]/g, '');
                setValues((v) => ({ ...v, localPhone: cleaned }));
              }}
              error={!!errors.phone}
              helperText={phoneHelperText}
              placeholder={phonePlaceholder}
              inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                  }
                }
              }}
            />
          </Box>
          <TextField 
            fullWidth 
            label="Mot de passe *" 
            type={showPassword ? 'text' : 'password'} 
            value={values.password} 
            onChange={setField('password')} 
            error={!!errors.password} 
            helperText={errors.password} 
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                }
              }
            }} 
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Afficher/masquer le mot de passe"
                    onClick={() => setShowPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }} 
          />
          
          {/* Indicateur de force du mot de passe */}
          {values.password && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{ marginBottom: 16 }}
            >
              <Typography variant="caption" sx={{ color: '#6B7280', mb: 1, display: 'block' }}>
                Critères de sécurité :
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: passwordStrength.minLength ? '#10B981' : '#EF4444' 
                  }} />
                  <Typography variant="caption" sx={{ color: passwordStrength.minLength ? '#10B981' : '#EF4444' }}>
                    Au moins 8 caractères
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: passwordStrength.hasUpperCase ? '#10B981' : '#EF4444' 
                  }} />
                  <Typography variant="caption" sx={{ color: passwordStrength.hasUpperCase ? '#10B981' : '#EF4444' }}>
                    Une lettre majuscule
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: passwordStrength.hasLowerCase ? '#10B981' : '#EF4444' 
                  }} />
                  <Typography variant="caption" sx={{ color: passwordStrength.hasLowerCase ? '#10B981' : '#EF4444' }}>
                    Une lettre minuscule
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: passwordStrength.hasNumbers ? '#10B981' : '#EF4444' 
                  }} />
                  <Typography variant="caption" sx={{ color: passwordStrength.hasNumbers ? '#10B981' : '#EF4444' }}>
                    Un chiffre
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: passwordStrength.hasSpecialChar ? '#10B981' : '#EF4444' 
                  }} />
                  <Typography variant="caption" sx={{ color: passwordStrength.hasSpecialChar ? '#10B981' : '#EF4444' }}>
                    Un caractère spécial (!@#$%^&*...)
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          )}
          <TextField 
            fullWidth 
            label="Confirmer le mot de passe *" 
            type={showConfirmPassword ? 'text' : 'password'} 
            value={values.confirmPassword} 
            onChange={setField('confirmPassword')} 
            error={!!errors.confirmPassword} 
            helperText={errors.confirmPassword} 
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)'
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 20px rgba(79, 70, 229, 0.25)'
                }
              }
            }} 
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Afficher/masquer la confirmation"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }} 
          />
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              disabled={submitting} 
              sx={{ 
                textTransform: 'none', 
                py: 1.2,
                bgcolor: '#4F46E5',
                boxShadow: '0 4px 20px rgba(79, 70, 229, 0.4)',
                '&:hover': { 
                  bgcolor: '#4338CA',
                  boxShadow: '0 6px 30px rgba(79, 70, 229, 0.5)'
                }
              }}
            >
            {submitting ? <CircularProgress size={22} sx={{ color: 'white' }} /> : "Créer mon compte"}
          </Button>
          </motion.div>
        </Box>
        </motion.div>
      </Box>

      {/* Footer with Glassmorphism */}
      <Box 
        sx={{ 
          width: '100%', 
          bgcolor: 'rgba(17, 24, 39, 0.95)', 
          backdropFilter: 'blur(10px)',
          color: '#9CA3AF', 
          py: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
          <Typography variant="body2">© 2025 Qilinsa Amplify. Tous droits réservés.</Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                onClick={scrollToSignup} 
                sx={{ 
                  color: '#9CA3AF', 
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    color: '#fff',
                    transform: 'translateY(-2px)'
                  } 
                }}
              >
                Contact
              </Button>
              <Button 
                onClick={scrollToSignup} 
                sx={{ 
                  color: '#9CA3AF', 
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    color: '#fff',
                    transform: 'translateY(-2px)'
                  } 
                }}
              >
                Aide
              </Button>
              <Button 
                onClick={scrollToSignup} 
                sx={{ 
                  color: '#9CA3AF', 
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    color: '#fff',
                    transform: 'translateY(-2px)'
                  } 
                }}
              >
                CGU
              </Button>
              <Button 
                component="a"
                href="https://qilinsa.com/politique-de-confidentialite/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: '#9CA3AF', 
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    color: '#fff',
                    transform: 'translateY(-2px)'
                  } 
                }}
              >
                Politique de confidentialité
              </Button>
          </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Success Popup with Animations */}
      <Dialog
        open={successOpen}
        onClose={() => { setSuccessOpen(false); navigate('/login'); }}
        PaperProps={{
          component: motion.div,
          initial: { scale: 0.8, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0.8, opacity: 0 },
          transition: { duration: 0.3, ease: "easeOut" },
          sx: {
            borderRadius: 3,
            p: 4,
            maxWidth: 420,
            width: '100%',
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 60px rgba(79, 70, 229, 0.3)',
            border: '1px solid rgba(79, 70, 229, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 2 }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: '#4F46E5' }}>
              🎉 Inscription réussie !
            </Typography>
          </motion.div>
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <Typography sx={{ color: '#4B5563' }}>
            Vous êtes bien inscrit. Vous recevrez un email de confirmation et serez contacté prochainement.
          </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 0, justifyContent: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
          <Button
            onClick={() => { setSuccessOpen(false); navigate('/login'); }}
            sx={{
              px: 3,
              py: 1,
              bgcolor: '#4F46E5',
              color: '#fff',
              textTransform: 'none',
                boxShadow: '0 4px 20px rgba(79, 70, 229, 0.4)',
                '&:hover': { 
                  bgcolor: '#4338CA',
                  boxShadow: '0 6px 30px rgba(79, 70, 229, 0.5)'
                },
            }}
            variant="contained"
          >
            Fermer
          </Button>
          </motion.div>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


